let ctx;
let muted = false;

try {
  muted = localStorage.getItem("marsilya_mute") === "1";
} catch {
  muted = false;
}

export const isSfxMuted = () => muted;

export const setSfxMuted = (value) => {
  muted = Boolean(value);
  try {
    localStorage.setItem("marsilya_mute", muted ? "1" : "0");
    const raw = JSON.parse(localStorage.getItem("marsilya-prefs") || "{}");
    raw.muteSfx = muted;
    localStorage.setItem("marsilya-prefs", JSON.stringify(raw));
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    document.querySelectorAll("audio, video").forEach((el) => {
      el.muted = muted;
    });
    window.dispatchEvent(new CustomEvent("marsilya-mute", { detail: muted }));
  }
};

const ensure = () => {
  if (typeof window === "undefined" || muted) return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

const tone = (freq, dur, type = "sine", gain = 0.08, delay = 0) => {
  const c = ensure();
  if (!c) return;
  const t = c.currentTime + delay;
  const osc = c.createOscillator();
  const amp = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  amp.gain.setValueAtTime(gain, t);
  amp.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(amp);
  amp.connect(c.destination);
  osc.start(t);
  osc.stop(t + dur + 0.03);
};

const noise = (dur, gain = 0.12) => {
  const c = ensure();
  if (!c) return;
  const buffer = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const src = c.createBufferSource();
  const filter = c.createBiquadFilter();
  const amp = c.createGain();
  src.buffer = buffer;
  filter.type = "lowpass";
  filter.frequency.value = 900;
  amp.gain.value = gain;
  src.connect(filter);
  filter.connect(amp);
  amp.connect(c.destination);
  src.start();
};

export const sfx = {
  click: () => tone(880, 0.05, "square", 0.045),
  tick: () => tone(1200 + Math.random() * 280, 0.028, "square", 0.028),
  safe: () => {
    tone(660, 0.08, "triangle", 0.07);
    tone(990, 0.1, "triangle", 0.05, 0.05);
  },
  boom: () => {
    noise(0.32, 0.18);
    tone(86, 0.26, "sawtooth", 0.1);
  },
  roll: () => noise(0.16, 0.07),
  win: () => {
    tone(523, 0.1);
    tone(659, 0.12, "sine", 0.07, 0.09);
    tone(784, 0.18, "sine", 0.08, 0.18);
  },
  lose: () => {
    tone(210, 0.14, "sawtooth", 0.06);
    tone(130, 0.2, "sawtooth", 0.05, 0.1);
  },
  cash: () => {
    tone(784, 0.09);
    tone(1046, 0.16, "sine", 0.07, 0.07);
  },
  plinkoDrop: () => {
    tone(420, 0.06, "triangle", 0.05);
    tone(680, 0.08, "sine", 0.04, 0.04);
  },
  plinkoPeg: () => tone(940 + Math.random() * 220, 0.022, "square", 0.022),
  cardDeal: () => {
    noise(0.035, 0.055);
    tone(165 + Math.random() * 35, 0.07, "triangle", 0.065);
    tone(82, 0.09, "sine", 0.038, 0.018);
  },
  cardFlip: () => {
    noise(0.025, 0.038);
    tone(480 + Math.random() * 80, 0.045, "sine", 0.032);
  },
  plinkoLand: () => {
    tone(260, 0.08, "triangle", 0.05);
    tone(520, 0.12, "sine", 0.05, 0.05);
  },
  limboTick: () => tone(680 + Math.random() * 120, 0.018, "sine", 0.018),
  coinFlip: () => {
    noise(0.08, 0.05);
    tone(740, 0.05, "square", 0.035);
  },
  coinLand: () => tone(320, 0.09, "triangle", 0.05),
  caseSpinTick: () => tone(640 + Math.random() * 80, 0.014, "square", 0.018),
  caseReveal: (amount = 0) => {
    const win = Number(amount) > 0;
    if (!win) {
      tone(220, 0.14, "triangle", 0.045);
      tone(140, 0.18, "sine", 0.035, 0.02);
      return;
    }
    noise(0.04, 0.04);
    tone(740, 0.07, "sine", 0.055);
    tone(988, 0.14, "sine", 0.06, 0.05);
    if (amount >= 200) {
      tone(1174, 0.18, "sine", 0.065, 0.06);
    }
  },
  wheelStart: () => tone(180, 0.12, "sawtooth", 0.04),
  outcome: (data = {}) => {
    if (data.refund) return sfx.cash();
    const stake = Number(data.bet ?? data.amount) || 0;
    const payout = Number(data.payout) || 0;
    const mult = Number(data.multiplier) || (stake > 0 && payout > 0 ? payout / stake : 0);
    const win = data.won === true
      ? mult >= 1
      : stake > 0
        ? payout >= stake && mult >= 1
        : payout > 0 && mult >= 1;
    if (win) return sfx.win();
    return sfx.lose();
  },
};

export default sfx;
